import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';

import { CompletedMemberDataExport } from 'apps-common/types';
import { logger } from 'apps-common/utils/logger';
import { DownloadIcon } from 'ui/components/Icons';

import { useGetMemberDataExportRedirect } from '../../hooks/useGetMemberDataExportRedirect';

const Button = styled.button(
  ({ theme }) => `
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    height: 100%;
    width: 45px;
    display: flex;
    justify-content: center;
    align-items: center;

    svg path {
      fill: ${theme.colors.white2};
    }

    &:disabled {
      opacity: 0.5;
      pointer-events: none;
    }
    
     &:focus-visible {
        outline: 3px solid ${theme.colors.focusOutline};
    }
  `,
);

export const DownloadButton = ({ dataExport }: { dataExport: CompletedMemberDataExport }) => {
  const queryClient = useQueryClient();
  const { data: href, refetch, isFetching } = useGetMemberDataExportRedirect(dataExport);

  const { completedAt, id } = dataExport;

  useEffect(() => {
    if (href) {
      const a = document.createElement('a');
      a.href = href;
      a.download = `data-export-${completedAt}.csv`; // TODO: Change to dataCreated once the API is updated
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      logger.debug('Download started for member data export', { href });
    }
  }, [href, completedAt]);

  const handleClick = async () => {
    // Clean possible errors from the query cache for other exports
    // TODO: If there is previous error, this will make the page jump when the error is removed. Fix this.
    await queryClient.resetQueries({
      queryKey: ['useGetMemberDataExportRedirect'],
      predicate: (query) => !query.queryKey.includes(id),
    });
    await refetch();
  };

  return (
    <Button disabled={isFetching} onClick={handleClick} aria-label="Download data">
      <DownloadIcon />
    </Button>
  );
};
